<template>
    <div class="head_tabs">
        <div class="h_tabs" @contextmenu="handleTabContextMenu">
          <el-tabs
              v-model="HeaderTabsActiveName"
              type="card"
              @tab-click="handlePathSwicth"
              class="handleTabs"
              closable
              @tab-remove="removeHeaderTabsList"
          > 
              <el-tab-pane
              v-for="(item,index) in HeaderTabsList"
              :key="item.path"
              :label="item.topic"
              :name="item.path"
              >
              </el-tab-pane>
          </el-tabs>
        </div>
        <div class="tabs-content">
            <router-view v-slot="{ Component }">
                <keep-alive :include="cachedViews">
                    <component :is="Component" />
                </keep-alive>
            </router-view>
        </div>
        <!-- 标签右键菜单：关闭其他/关闭右侧（作用于被右键的标签） -->
        <div
            v-if="ctxMenu.visible"
            class="tabs_ctx_menu"
            :style="{ left: ctxMenu.x + 'px', top: ctxMenu.y + 'px' }"
        >
            <div
                class="ctx_item"
                :class="{ 'is-disabled': HeaderTabsList.length <= 1 }"
                @click="handleCtxCommand('closeOther')"
            >关闭其他页面</div>
            <div
                class="ctx_item"
                :class="{ 'is-disabled': isCtxLastTab }"
                @click="handleCtxCommand('closeRight')"
            >关闭右侧页面</div>
        </div>
    </div>
</template>
<script setup>
import { ref ,watch,computed, onMounted, onBeforeUnmount, nextTick} from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex';
// 声明路由对象
const router = useRouter();
// 声明vuex对象
const store = useStore();

const HeaderTabsActiveName = ref('')
const HeaderTabsList = ref([])

// 缓存的视图列表，用于keep-alive的include属性
// 依赖于HeaderTabsActiveName，确保切换标签时重新计算
const cachedViews = computed(() => {
  // 依赖HeaderTabsActiveName，确保切换标签时重新计算
  const _activeName = HeaderTabsActiveName.value;
  
  const allRoutes = router.getRoutes();
  
  const views = HeaderTabsList.value
    .map(item => {
      // 使用HeaderTabsList中的path来查找对应的路由
      let routePath = item.path;
      
      // 查找匹配的路由
      let route = allRoutes.find(r => r.path === routePath);
      
      // 如果路由有redirect，查找被重定向的路由
      if (route?.redirect && typeof route.redirect === 'string') {
        const redirectRoute = allRoutes.find(r => r.path === route.redirect);
        
        // 如果重定向的路由有子路由，从子路由获取name
        if (redirectRoute?.children?.length > 0) {
          const childRoute = redirectRoute.children.find(c => c.path === '' || c.path === route.redirect);
          if (childRoute?.name) {
            return childRoute.name;
          }
        }
        
        // 如果重定向的路由本身有name，使用它
        if (redirectRoute?.name) {
          return redirectRoute.name;
        }
      }
      
      // 如果路由没有name，尝试从子路由中获取name
      if (!route?.name && route?.children?.length > 0) {
        const childRoute = route.children.find(c => c.path === '' || c.path === routePath);
        if (childRoute?.name) {
          return childRoute.name;
        }
      }
      
      return route?.name || '';
    })
    .filter(name => name && typeof name === 'string');
  
  return views;
})

// 标签右键菜单状态：visible/坐标/被右键的标签path
const ctxMenu = ref({ visible: false, x: 0, y: 0, path: '' })

// 定义事件发射函数
const emit = defineEmits(['openMenu'])

onMounted(()=>{
  // 这里的数据都是在vuex中获取的
  HeaderTabsList.value = store.state.HeaderTabs;
  HeaderTabsActiveName.value = store.state.TabsActiveName;
  // 右键菜单的全局关闭监听
  document.addEventListener('click', closeCtxMenu);
  document.addEventListener('contextmenu', closeCtxMenu);
  window.addEventListener('resize', closeCtxMenu);
  window.addEventListener('blur', closeCtxMenu);
  document.addEventListener('keydown', onCtxKeydown);
})

// 监听Vuex中的HeaderTabs变化，保持同步
const getHeaderTabs = computed(()=>{
  return store.state.HeaderTabs;
})

watch(getHeaderTabs, (newVal) => {
  HeaderTabsList.value = newVal;
}, { deep: true, immediate: true })

onBeforeUnmount(()=>{
  document.removeEventListener('click', closeCtxMenu);
  document.removeEventListener('contextmenu', closeCtxMenu);
  window.removeEventListener('resize', closeCtxMenu);
  window.removeEventListener('blur', closeCtxMenu);
  document.removeEventListener('keydown', onCtxKeydown);
})

// 这里直接监听vuex的数据会导致失效，这里可能是因为vue性能问题，vue的底层没有给vuex的数据也进行数据绑定吧
// 因此，这里使用computed这个api函数来返回vuex中的值，然后将此方法体放入watch中监听
const getTabsActiveName = computed(()=>{
	return store.state.TabsActiveName;
})

// 使用watch监听vuex的数据，并开启第一次绑定监听--immediate
watch(getTabsActiveName,(o1)=>{
  if(o1){
    // 我们每添加条数据都需要修改相关的tabs索引，否则选中项是不会高亮的
    HeaderTabsActiveName.value = o1;
  }
},{ immediate: true })

// 标签点击触发
const handlePathSwicth = (targetName)=>{
  let name = targetName.props.name;
  // 如果点击的是当前已激活的标签，则不执行任何操作，避免刷新页面
  if(name === HeaderTabsActiveName.value){
    return;
  }
  store.commit("updateTabsActiveName",name);
  HeaderTabsActiveName.value = name;
  // 触发自定义事件并传递 url 参数
  emit('openMenu', name);
  
  // 检查路由是否有redirect，如果有则直接跳转到redirect的目标路径
  const allRoutes = router.getRoutes();
  const route = allRoutes.find(r => r.path === name);
  if (route?.redirect && typeof route.redirect === 'string') {
    // 如果路由有redirect，直接跳转到redirect的目标路径
    name = route.redirect;
  }
  
  // 只有当路由路径不同时才执行跳转
  if(router.currentRoute.value.path !== name){
    router.push({
      path:name
    })
  }
}
// 删除tabs触发
const removeHeaderTabsList = (targetName) => {
  // 使用vuex中自定义的方法删除数据
  store.commit('removeHeaderTabs', targetName)

  setTimeout(()=>{
    // 利用定时器达到延时方法，防止HeaderTabsActiveName还未变就执行下面的跳转
    router.push({
      path:HeaderTabsActiveName.value
    })
  },0)
}

// 标签右键：通过事件代理找到被右键的标签，弹出操作菜单
const handleTabContextMenu = (e)=>{
  const tabEl = e.target.closest('.el-tabs__item');
  if(!tabEl) return;
  const name = tabEl.id.replace(/^tab-/, '');
  const tab = HeaderTabsList.value.find(t=>t.path == name);
  if(!tab) return;
  e.preventDefault();
  // 阻止冒泡，避免触发document上的关闭监听导致菜单刚弹出就被关闭
  e.stopPropagation();
  ctxMenu.value = { visible: true, x: e.clientX, y: e.clientY, path: tab.path };
  nextTick(fixCtxMenuPosition);
}

// 防止右键菜单超出视口边界
const fixCtxMenuPosition = ()=>{
  const el = document.querySelector('.tabs_ctx_menu');
  if(!el) return;
  let { x, y } = ctxMenu.value;
  const rect = el.getBoundingClientRect();
  if(x + rect.width > window.innerWidth){
    x = window.innerWidth - rect.width - 4;
  }
  if(y + rect.height > window.innerHeight){
    y = window.innerHeight - rect.height - 4;
  }
  ctxMenu.value.x = x;
  ctxMenu.value.y = y;
}

// 被右键的标签是否为最后一个，用于禁用"关闭右侧页面"
const isCtxLastTab = computed(()=>{
  const list = HeaderTabsList.value;
  const index = list.findIndex(t=>t.path == ctxMenu.value.path);
  return index == -1 || index == list.length - 1;
})

// 右键菜单操作：关闭其他/关闭右侧（作用于被右键的标签）
const handleCtxCommand = (command)=>{
  const path = ctxMenu.value.path;
  closeCtxMenu();
  if(command == 'closeOther'){
    if(HeaderTabsList.value.length <= 1) return;
    store.commit('closeOtherTabs', path);
  }else if(command == 'closeRight'){
    if(isCtxLastTab.value) return;
    store.commit('closeRightTabs', path);
  }
  // 若当前激活标签被关闭了（激活页已切换），则跳转到最新的激活标签
  if(router.currentRoute.value.path != store.state.TabsActiveName){
    router.push({
      path:store.state.TabsActiveName
    })
  }
}

const closeCtxMenu = ()=>{
  ctxMenu.value.visible = false;
}

const onCtxKeydown = (e)=>{
  if(e.key === 'Escape'){
    closeCtxMenu();
  }
}
</script>
<style lang="scss">
.head_tabs {
    display: flex;
    flex-direction: column;
    height: 100%;

    .h_tabs {
        position: sticky;
        top: 0;
        z-index: 100;
        background-color: #fff;
        flex-shrink: 0;
        display: flex;
        align-items: center;

        .handleTabs {
            flex: 1;
            min-width: 0;
        }
    }

    .tabs-content {
        flex: 1;
        overflow-y: auto;
        padding: 12px;
    }
}

.h_tabs .el-tabs__nav{
    background-color: #fff;
}

.tabs_ctx_menu {
    position: fixed;
    z-index: 3000;
    min-width: 140px;
    padding: 5px 0;
    background-color: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);

    .ctx_item {
        padding: 7px 16px;
        font-size: 13px;
        line-height: 1.5;
        color: #606266;
        cursor: pointer;
        white-space: nowrap;

        &:hover {
            background-color: #f5f7fa;
            color: #409eff;
        }

        &.is-disabled {
            color: #c0c4cc;
            cursor: not-allowed;

            &:hover {
                background-color: transparent;
                color: #c0c4cc;
            }
        }
    }
}
</style>